import React, { useEffect, useRef, useState } from 'react';
import { preflightAPICall, processAPIError, processAPIQuoteError, translate } from 'utils';
import { Button, Input } from '@insly/qmt-reactjs-ui-lib';
import {
  SMSConfirmDesc,
  SMSConfirmInputDesc,
  SMSConfirmModal,
  SMSConfirmResendTimer,
  Controls,
  SMSConfirmWaitingText
} from './styles';
import { useApp } from '../../../context/App';
import { Loader } from '../../../styles/common';
import { renderErrors } from '../common/Error';
import axios from 'axios';
import { TError } from 'models';
import { getRequestStatus } from 'config/api';

// STEPS:
// 0 - Request SMS code
// 1 - SMS code sent, waiting for user input
// 2 - User entered code, checking it
// 3 - Code is correct, success

let timerTimeout: ReturnType<typeof setTimeout> ;

export const SMSConfirm = ({
  itemId,
  // phoneNumber,
  onSuccess,
  onSMSSent,
  successMessage,
  status,
  apiURL,
}:{
  itemId: string,
  // phoneNumber: string,
  onSuccess: () => void,
  onSMSSent: () => void,
  successMessage?: string,
  status: number,
  apiURL: string,
}) => {
  const [step, setStep] = useState(0);
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<TError[]>([]);
  const [phoneNumber, setPhoneNumber] = useState('');

  //Add 30s timer for SMS code resend
  const [timer, setTimer] = useState(0);

  const { showNotification, hideDialog, dialogProps } = useApp();

  // getRequestStatus callbacks fire after the dialog can be dismissed; gate
  // every setState behind this ref to avoid leaking updates on unmount.
  const isMountedRef = useRef(true);
  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    if (dialogProps.show) {
      if (status === 5) {
        setStep(1);
      } else {
        requestSMSCode();
      }
    } else {
      setErrors([]);
      setCode('');
    }

  }, [dialogProps.show]);

  React.useEffect(() => {
    // Clear the timer when component unmounts or when timer changes
    clearTimeout(timerTimeout);
    timerTimeout = setTimeout(() => {
      setTimer(prev => {
        if (prev <= 1) {
          return 0;
        }
        // Decrement timer every second
        return prev - 1;
      });
    }, 1000);

  }, [timer]);

  const requestSMSCode = () => {
    setErrors([]);
    setStep(0);
    preflightAPICall(() => {
      axios.post(`${apiURL}/${itemId}/sms/send`,
      ).then(response => {
        // Newer endpoints (e.g. CEPIK) return { request_id } and need polling via getRequestStatus.
        // Older endpoints return the phone number directly in the response body — keep that
        // path until those callers migrate.
        if (response.data?.request_id) {
          getRequestStatus('quote', response.data.request_id, (data) => {
            if (!isMountedRef.current) return;
            const result = (data as unknown as { response?: { phone_number?: string } | string }).response;
            const phoneNumber = typeof result === 'string' ? result : result?.phone_number;
            setPhoneNumber(phoneNumber || '');
            setStep(1);
            setTimer(30);
            onSMSSent();
          }, (error) => {
            console.error('SMS send failed:', error?.response?.status);
            if (!isMountedRef.current) return;
            setErrors(processAPIQuoteError(error, true) as TError[]);
            // Keep step at 0 — advancing to step 1 would ask the user to enter
            // a code that was never sent.
            setTimer(0);
          });
        } else {
          setPhoneNumber(response.data);
          setStep(1);
          setTimer(30);
          onSMSSent();
        }
      }).catch(error => {
        console.error('SMS send failed:', error?.response?.status);
        if (!isMountedRef.current) return;
        setErrors(processAPIError(error, true) as TError[]);
        setTimer(0);
      });
    });
  };

  const handleCheckSMSCode = () => {
    setStep(2);
    preflightAPICall(() => {
      axios.post(`${apiURL}/${itemId}/sms/approve`,
        { code }
      ).then(response => {
        const onApproveSuccess = () => {
          setStep(3);
          hideDialog();
          showNotification({
            autoHide: true,
            preset: 'success',
            message: successMessage || translate({ key: 'dialog.sms_confirm.success' })
          });
          onSuccess();
        };

        if (response.data?.request_id) {
          getRequestStatus('quote', response.data.request_id, () => {
            if (!isMountedRef.current) return;
            onApproveSuccess();
          }, (error) => {
            console.error('SMS approve failed:', error?.response?.status);
            if (!isMountedRef.current) return;
            setErrors(processAPIQuoteError(error, true) as TError[]);
          });
        } else {
          onApproveSuccess();
        }
      }).catch(error => {
        console.error('SMS approve failed:', error?.response?.status);
        if (!isMountedRef.current) return;
        setErrors(processAPIError(error, true) as TError[]);
      });
    });
  };

  if (errors.length > 0) {
    return (
      <SMSConfirmModal $centered >
        {renderErrors(errors)}
        <br />
        {step === 2 && (
          <Button size="l" onClick={() => {
            setErrors([]);
            setStep(1); // Reset to step 1 to allow re-entering the code
          }}>
            {translate({ key: 'dialog.sms_confirm.go_back' })}
          </Button>
        )}
      </SMSConfirmModal>
    );
  }

  if ([0, 2].includes(step)) {
    return (
      <SMSConfirmModal $centered >
        <Loader />
        <SMSConfirmWaitingText>
          {translate({ key: 'dialog.sms_confirm.waiting_text' })}
        </SMSConfirmWaitingText>
      </SMSConfirmModal>
    );
  }

  return (
    <SMSConfirmModal>
      <SMSConfirmDesc>{translate({ key: 'dialog.sms_confirm.desc', replace:[phoneNumber] })}</SMSConfirmDesc>
      <Input
        autoComplete="off"
        label={translate({ key: 'dialog.sms_confirm.input_code.label' })}
        value={code}
        maxLength={6}
        handleChange={(name, value) => setCode(value?.toUpperCase() as string)}
      />
      <SMSConfirmInputDesc>
        {translate({ key: 'dialog.sms_confirm.input_code.desc' })}
      </SMSConfirmInputDesc>

      {step === 1 && (
        <SMSConfirmResendTimer onClick={timer === 0 ? () => requestSMSCode() : undefined} $clickable={timer === 0}>
          {translate({ key: 'dialog.sms_confirm.timer_text' })} {timer > 0 && `(${timer}s)`}
        </SMSConfirmResendTimer>
      )}

      <Controls>
        {step === 1 && (
          <Button size="l" onClick={handleCheckSMSCode} disabled={code?.length !== 6}>
            {translate({ key: 'dialog.sms_confirm.submit_code' })}
          </Button>
        )}
      </Controls>
    </SMSConfirmModal>
  );
};

export const setSMSDialogProps = ({
  itemId,
  apiUrl,
  status,
  onSuccess,
  onSMSSent,
  successMessage,
  onDecline,
} : {
  itemId: string,
  apiUrl: string,
  status: number,
  onSuccess: () => void,
  onSMSSent: () => void,
  successMessage?: string,
  onDecline?: () => void,
}) => ({
  title: translate({ key: 'dialog.sms_confirm.title' }),
  showCloseIcon: true,
  body: <SMSConfirm
    itemId={itemId}
    onSuccess={onSuccess}
    onSMSSent={onSMSSent}
    successMessage={successMessage}
    status={status}
    apiURL={apiUrl}
  />,
  hideIcon: true,
  hideControls: true,
  preventClose: true,
  onDecline: onDecline,
  style: {
    minWidth: '50vw',
    padding: '8px',
  }
});
